import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { BottomTabBarOptions } from '@react-navigation/bottom-tabs/src/types';
import { useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { StackNavigationOptions } from '@react-navigation/stack/src/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useDispatch } from 'react-redux';
import HeaderBar from './components/global/HeaderBar';
import NavigationTitle from './components/global/NavigationTitle';
import QuestionnaireHeaderLeft from './components/questionnaire/QuestionnaireHeaderLeft';
import QuestionnaireHeaderRight from './components/questionnaire/QuestionnaireHeaderRight';
import QuestionnaireHeaderTitle from './components/questionnaire/QuestionnaireHeaderTitle';
import { SelectedSite } from './components/site/Site/Outlet/type';
import NewSiteScreen from './components/site/Site/SiteNew';
import CalibrationPage from './routes/calibration';
import CreateCompanyPage from './routes/createcompany';
import HomeTab from './routes/home';
import LandingPage from './routes/landing';
import LicensesPage from './routes/licenses';
import LobbyPage from './routes/lobby';
import PayoffPage from './routes/payoff';
import QuestionnairePage from './routes/questionnaire';
import QuestionReferencePage from './routes/questionreference';
import ReportsTabNavigator from './routes/reports';
import SettingsTab from './routes/settings';
import SiteReportFilter from './routes/SiteReportFilter';
import SubmitReportPage from './routes/submitreport';
import UploadReportPage from './routes/uploadreport';
import WebViewPage from './routes/webview';
import { setActiveTab } from './store/reducers/site/site';
import * as siteReportActions from './store/reducers/siteReport/siteReport.action';
import { User } from './utils/classes';
import fontMaker from './utils/font';
import { SCREEN_WIDTH, HOME_SHORT_ZOOM } from './utils/screensize';

const getTabBarVisibility = (route: any): boolean => {
  try {
    if (
      !route?.state?.stale &&
      (route?.state?.index > 0 ||
        route?.state?.routes[0]?.name === 'Payoff' ||
        route?.state?.routes[0]?.name === 'SubmitReport')
    ) {
      return false;
    }
    return true;
  } catch {
    return true;
  }
};

const defaultStackOptions: StackNavigationOptions = {
  gestureEnabled: false,
  headerBackTitleVisible: false,
  headerTintColor: '#ffffff',
  headerStyle: {
    borderBottomWidth: 0,
    backgroundColor: '#3cd070',
    shadowColor: '#919191',
    shadowRadius: 5,
    shadowOpacity: 1,
    shadowOffset: { width: 1, height: 1 }
  },
  headerTitleStyle: {
    color: '#ffffff',
    fontFamily: 'Poppins',
    ...fontMaker({ weight: 'SemiBold' }),
    textAlign: 'center',
    fontSize: 10
  },
  headerTitleAlign: 'center',
  headerRight: () => <View />
};

export type AuthStackParamList = {
  Landing: undefined;
};

const AuthStack = createStackNavigator();

export const AuthStackNavigator: React.ComponentType<any> = () => {
  return (
    <AuthStack.Navigator initialRouteName="Landing" screenOptions={defaultStackOptions}>
      <AuthStack.Screen name="Landing" component={LandingPage} options={{ headerShown: false }} />
    </AuthStack.Navigator>
  );
};

export type WebViewStackParams = {
  WebView: { profileColor: string; title: string; url: string; purpose: string; injectedJsScript: string };
};

export type HomeStackParamList = {
  Home: undefined;
  Site: { profileColor: string; originTab: string; siteName: string; selectedScheduleKey?: string };
  Calibration: { profileColor: string; originTab: string; siteName: string };
  Lobby: { profileColor: string; originTab: string; siteName: string; backHandler?: () => any };
  Questionnaire: { profileColor: string; originTab: string; siteName: string };
  QuestionReference: { profileColor: string; originTab: string; siteName: string; url: string };
  SubmitReport: { profileColor: string; originTab: string; siteName: string; members?: { [uid: string]: User } };
  UploadReport: { profileColor: string; originTab: string; siteName: string };
  Payoff: { profileColor: string; originTab: string; siteName: string };
  CreateCompany: { profileColor: string; originTab: string; siteName: string };
  ReportFilter: { profileColor: string; selectedSite: SelectedSite };
};

const HomeStack = createStackNavigator<HomeStackParamList & WebViewStackParams>();

const headerLeftIcon = () => (
  <Icon
    name={Platform.OS === 'ios' ? 'chevron-left' : 'arrow-left'}
    size={Platform.OS === 'ios' ? 32 : 24}
    color="white"
  />
);

const HomeStackNavigator: React.ComponentType<any> = () => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const { t } = useTranslation(['site', 'common', 'company']);

  const onPressBack = () => {
    navigation.goBack();
    dispatch(setActiveTab('reports'));
    dispatch(siteReportActions.setBackTemporaryReport());
  };

  const onPrevious = () => {
    navigation.goBack();
  };

  const onPressReset = (params: SelectedSite) => {
    dispatch(siteReportActions.setResetTemporaryReport());
  };

  return (
    <HomeStack.Navigator initialRouteName="Home" screenOptions={defaultStackOptions}>
      <HomeStack.Screen
        name="Home"
        component={HomeTab}
        options={{
          gestureEnabled: false,
          headerShown: false
        }}
      />
      <HomeStack.Screen
        name="WebView"
        component={WebViewPage}
        options={({ route }) => ({
          gestureEnabled: false,
          headerStyle: {
            backgroundColor: route.params ? route.params.profileColor : '#3CD070'
          },
          headerTitle: () => {
            if (!route.params.title) {
              return;
            }
            return <NavigationTitle defaultTintColor="white" title={route.params?.title} />;
          }
        })}
      />
      <HomeStack.Screen
        name="Site"
        component={NewSiteScreen}
        options={({ route }) => {
          return {
            tabBarVisible: false,
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => (
              <NavigationTitle
                defaultTintColor="white"
                title={route.params ? route.params?.siteName : t('common:unknownSite')}
              />
            ),
            headerLeft: () => (
              <TouchableOpacity
                delayPressIn={0}
                onPress={() =>
                  navigation.reset({
                    index: 0,
                    routes: [{ name: 'HomeTab' }]
                  })
                }
                style={[styles.backButton, styles.backButtonLeftPosition]}
              >
                {headerLeftIcon()}
              </TouchableOpacity>
            )
          };
        }}
      />
      <HomeStack.Screen
        name="ReportFilter"
        component={SiteReportFilter}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerRight: () => (
              <TouchableOpacity
                style={styles.backButton}
                onPress={() => onPressReset(route.params?.selectedSite)}
                delayPressIn={0}
              >
                <Icon name="refresh" size={28} color="#ffffff" style={{ transform: [{ rotateY: '180deg' }] }} />
              </TouchableOpacity>
            ),
            headerTitle: () => <NavigationTitle defaultTintColor="white" title={t('site:siteReports.filter')} />,
            headerLeft: () => (
              <TouchableOpacity style={styles.backButton} onPress={onPressBack} delayPressIn={0}>
                <Icon name="close" size={28} color="#ffffff" />
              </TouchableOpacity>
            )
          };
        }}
      />
      <HomeStack.Screen
        name="Calibration"
        component={CalibrationPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            header: () => (
              <HeaderBar
                title={
                  route.params
                    ? { en: route.params.siteName, id: route.params.siteName }
                    : { en: 'Unknown Site', id: 'Unknown Site' }
                }
                themeColor={route.params ? route.params.profileColor : '#3CD070'}
              />
            )
          };
        }}
      />
      <HomeStack.Screen
        name="Lobby"
        component={LobbyPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => (
              <NavigationTitle
                defaultTintColor="white"
                title={route.params ? route.params?.siteName : t('common:unknownSite')}
              />
            ),
            headerLeft: () => (
              <TouchableOpacity onPress={route.params.backHandler} style={styles.backButton} delayPressIn={0}>
                {headerLeftIcon()}
              </TouchableOpacity>
            )
          };
        }}
      />
      <HomeStack.Screen
        name="Questionnaire"
        component={QuestionnairePage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3cd070'
            },
            headerTitle: () => (
              <QuestionnaireHeaderTitle siteName={route.params ? route.params?.siteName : t('common:unknownSite')} />
            ),
            headerRight: () => <QuestionnaireHeaderRight />,
            headerLeft: props => (
              <QuestionnaireHeaderLeft onPressBack={props.onPress!} />
            )
          };
        }}
      />
      <HomeStack.Screen
        name="QuestionReference"
        component={QuestionReferencePage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => (
              <NavigationTitle
                defaultTintColor="white"
                title={route.params ? route.params?.siteName : t('common:unknownSite')}
              />
            )
          };
        }}
      />
      <HomeStack.Screen
        name="SubmitReport"
        component={SubmitReportPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => (
              <NavigationTitle
                defaultTintColor="white"
                title={route.params ? route.params?.siteName : t('common:unknownSite')}
              />
            ),
            headerLeft: () => (
              <TouchableOpacity style={styles.backButton} onPress={onPrevious}>
                {headerLeftIcon()}
              </TouchableOpacity>
            )
          };
        }}
      />
      <HomeStack.Screen
        name="UploadReport"
        component={UploadReportPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => (
              <NavigationTitle
                defaultTintColor="white"
                title={route.params ? route.params?.siteName : t('common:unknownSite')}
              />
            ),
            headerLeft: () => (
              <TouchableOpacity style={styles.backButton} onPress={onPrevious}>
                {headerLeftIcon()}
              </TouchableOpacity>
            )
          };
        }}
      />
      <HomeStack.Screen
        name="Payoff"
        component={PayoffPage}
        options={{
          gestureEnabled: false,
          headerShown: false
        }}
      />
      <HomeStack.Screen
        name="CreateCompany"
        component={CreateCompanyPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => <NavigationTitle defaultTintColor="white" title={t('company:createCompany')} />,
            headerLeft: () => (
              <TouchableOpacity style={styles.backButton} onPress={onPrevious}>
                {headerLeftIcon()}
              </TouchableOpacity>
            )
          };
        }}
      />
    </HomeStack.Navigator>
  );
};

export type SettingsStackParamList = {
  Settings: { profileColor: string };
  Licenses: { profileColor: string };
};

const SettingsStack = createStackNavigator<SettingsStackParamList & WebViewStackParams>();

const SettingsStackNavigator: React.ComponentType<any> = () => {
  const { t } = useTranslation(['settings']);
  return (
    <SettingsStack.Navigator initialRouteName="Settings" screenOptions={defaultStackOptions}>
      <SettingsStack.Screen
        name="Settings"
        component={SettingsTab}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => <NavigationTitle defaultTintColor="white" title={t('settings:settings')} />
          };
        }}
      />
      <SettingsStack.Screen
        name="Licenses"
        component={LicensesPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => <NavigationTitle defaultTintColor="white" title={t('settings:softwareLicense')} />
          };
        }}
      />
      <SettingsStack.Screen
        name="WebView"
        component={WebViewPage}
        options={({ route }) => {
          return {
            gestureEnabled: false,
            headerStyle: {
              backgroundColor: route.params ? route.params.profileColor : '#3CD070'
            },
            headerTitle: () => <NavigationTitle defaultTintColor="white" title={route.params?.title} />
          };
        }}
      />
    </SettingsStack.Navigator>
  );
};

const defaultTabOptions: BottomTabBarOptions = {
  activeTintColor: '#555',
  activeBackgroundColor: '#fff',
  inactiveTintColor: '#ccc',
  inactiveBackgroundColor: '#fff',
  height: 75,
  style: {
    height: 75,
    paddingTop: SCREEN_WIDTH >= 768 ? 13 : 0,
    borderTopColor: 'transparent',
    borderTopWidth: 0,
    bottom: 0,
    marginHorizontal: 'auto',
    position: 'fixed',
    // Match the Home content zoom-out on short viewports so the bottom tab bar stays
    // proportional. `zoom` scales layout; with bottom:0 the bar stays flush at the
    // bottom. The zoom origin is top-left, so the bar shrinks toward the left — if it
    // ends up off-center vs the content column, replace `marginHorizontal: 'auto'` with
    // an explicit `left: (SCREEN_WIDTH - 768 * HOME_SHORT_ZOOM) / 2`. No-op (zoom: 1) on
    // normal/phone viewports.
    ...({ zoom: HOME_SHORT_ZOOM } as any),
    width: SCREEN_WIDTH < 1200 ? '100%' : 768,
    paddingLeft: SCREEN_WIDTH < 1200 ? 200 : 0,
    paddingRight: SCREEN_WIDTH < 1200 ? 200 : 0,
  },
  tabStyle: {
    display: SCREEN_WIDTH >= 1024 ? 'grid' : undefined,
    height: 75
  },
  labelStyle: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 14,
    marginHorizontal: 0,
    marginTop: SCREEN_WIDTH >= 750 ? 4 : -4,
    marginBottom: SCREEN_WIDTH > 500 ? 20 : 10,
    textAlign: 'center'
  }
};

export type AppTabsParamList = {
  HomeTab: {};
  ReportsTab: {};
  IssuesTab: {};
  ProfileTab: {};
  SettingsTab: {};
};

const AppTabs = createBottomTabNavigator<AppTabsParamList>();

export const AppTabsNavigator: React.ComponentType<any> = () => {
  const { t } = useTranslation(['home', 'settings']);
  return (
    <AppTabs.Navigator initialRouteName="HomeTab" tabBarOptions={defaultTabOptions}>
      <AppTabs.Screen
        name="HomeTab"
        component={HomeStackNavigator}
        options={({ route }) => ({
          tabBarVisible: getTabBarVisibility(route),
          tabBarLabel: ({ focused }: { focused: boolean; tintColor?: string; horizontal?: boolean }) => (
            <NavigationTitle
              style={defaultTabOptions.labelStyle}
              activeTintColor={defaultTabOptions.activeTintColor}
              defaultTintColor="#ccc"
              active={focused}
              title={t('home:home')}
            />
          ),
          tabBarIcon: ({ color, focused }) =>
            focused ? (
              <Icon name="home" size={26} color={color || 'white'} style={{ paddingTop: 5 }} />
            ) : (
              <Icon name="home-outline" size={26} color={color || 'white'} style={{ paddingTop: 5 }} />
            )
        })}
      />
      <AppTabs.Screen
        name="ReportsTab"
        component={ReportsTabNavigator}
        options={({ route }) => ({
          tabBarVisible: getTabBarVisibility(route),
          tabBarLabel: ({ focused }: { focused: boolean; tintColor?: string; horizontal?: boolean }) => (
            <NavigationTitle
              style={defaultTabOptions.labelStyle}
              activeTintColor={defaultTabOptions.activeTintColor}
              defaultTintColor="#ccc"
              active={focused}
              title={t('home:reports.tabLabel')}
            />
          ),
          tabBarIcon: ({ color, focused }) =>
            focused ? (
              <Icon name="file-document" size={26} color={color || 'white'} style={{ paddingTop: 5 }} />
            ) : (
              <Icon name="file-document-outline" size={26} color={color || 'white'} style={{ paddingTop: 5 }} />
            )
        })}
      />
      <AppTabs.Screen
        name="SettingsTab"
        component={SettingsStackNavigator}
        options={({ route }) => ({
          tabBarVisible: getTabBarVisibility(route),
          tabBarLabel: ({ focused }: { focused: boolean; tintColor?: string; horizontal?: boolean }) => (
            <NavigationTitle
              style={defaultTabOptions.labelStyle}
              activeTintColor={defaultTabOptions.activeTintColor}
              defaultTintColor="#ccc"
              active={focused}
              title={t('settings:settingsTitle')}
            />
          ),
          tabBarIcon: ({ color, focused }) =>
            focused ? (
              <Icon name="cog" size={26} color={color || 'white'} style={{ paddingTop: 5 }} />
            ) : (
              <Icon name="cog-outline" size={26} color={color || 'white'} style={{ paddingTop: 5 }} />
            )
        })}
      />
    </AppTabs.Navigator>
  );
};

const styles = StyleSheet.create({
  iconContainer: {
    paddingLeft: 14
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center'
  },
  backButtonLeftPosition: {
    marginLeft: 8
  },
  titleContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    alignSelf: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 8,
    width: SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH
  },
  navTitle: {
    fontSize: 17,
    marginHorizontal: 16
  },
  refreshContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
});
